import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import Flex from '@deprecated-ui/common/Flex';
import Text from '@deprecated-ui/common/Text';
import Button from '@deprecated-ui/common/Button';
import Modal from '@deprecated-ui/common/Modal';
import color from '@deprecated-ui/_style/color';
import Image from 'global/Image';
import If from 'global/ErrorHandlers/If';
import alertMessage from 'global/AlertMessage';
import { useRouter } from 'next/router';
import tradingModalStore from 'global/TradingModal/store';
import { sanitizeURL } from 'utils/general';
import {
  errorInfoList,
  ErrorModalConfig,
  tradingErrorModalConfig,
  tradingErrorModalList,
} from './constants';

interface ModalError {
  dataCy?: string;
  zIndex?: number;
  errorCode?: string | number;
  errorType?: string;
  errorMessage?: string;
  isErrorSecurities?: boolean;
  onLogout?: () => void;
  onClose?: () => void;
}

const ModalError: FC<ModalError> = ({
  dataCy = '',
  zIndex = 1000,
  errorCode = 0,
  errorType = '',
  errorMessage = '',
  isErrorSecurities = true,
  onLogout = () => {},
  onClose = () => {},
}) => {
  const [isVisible, setVisible] = useState(false);
  // access trading modal store
  const closeTradingModal = tradingModalStore((s) => s.resetModal);
  const isTradingModalVisible = tradingModalStore((s) => s.visible);

  const router = useRouter();

  const {
    title: modalErrorTitle,
    imageUrl,
    description,
    isShowButton,
    isLogoutUser,
    buttonText,
    redirectTo,
    needToCloseTradingModal,
    isFullWidthButton,
  } = useMemo<ErrorModalConfig>(() => {
    if (!isErrorSecurities) {
      return errorInfoList.find(({ error }) => error === errorCode) || {};
    }

    const modalConfigKey =
      tradingErrorModalList[errorCode]?.[errorType || 'NO_SERVER_RESPONSE'];
    return tradingErrorModalConfig[modalConfigKey] || {};
  }, [errorCode, errorType]);

  const handleModalCancel = () => {
    setVisible(false);
    onClose();

    if (isLogoutUser) onLogout();
  };

  const handleConfirmClick = () => {
    if (isLogoutUser) {
      onLogout();
    }

    if (redirectTo) {
      router.push(sanitizeURL(redirectTo));
    }

    setVisible(false);
    onClose();
  };

  const handleShowModal = useCallback(() => {
    if (!errorCode) return;

    // Show error modal if there is any modal error constant
    if (modalErrorTitle) {
      setVisible(true);
      return;
    }

    // Show error toast if there is no modal error constant
    // Specific case, due to existing issue on code that isShowAlertMessage not developed properly
    // We cannot handle isShowAlertMessage, because the requestConfigTrading is used widely
    // need to reconfirm to all api that use requestConfigTrading
    const isHideAlert = errorType !== 'INVALID_CREDS' && router.asPath.includes('securities/withdrawal');
    if (!modalErrorTitle && errorMessage && isErrorSecurities && isHideAlert) {
      alertMessage({
        content: errorMessage,
        alertType: 'plain-default',
        messageType: 'error',
        hasCloseIcon: false,
        onCloseHandler: onClose,
      });
    }
  }, [errorCode, errorType, errorMessage, modalErrorTitle]);

  useEffect(() => {
    handleShowModal();
    if (needToCloseTradingModal && isTradingModalVisible) {
      closeTradingModal();
    }
  }, [handleShowModal]);

  return (
    <Modal
      centered
      className={dataCy}
      visible={isVisible}
      onCancel={handleModalCancel}
      afterClose={onClose}
      width="400px"
      zIndex={zIndex}
      style={{ borderRadius: '4px' }}
    >
      <Flex
        direction="column"
        align="center"
        justify="center"
        padding="50px 40px"
      >
        <Image src={`${imageUrl}`} alt="info" width={140} />
        <Text
          data-cy="error-trading-modal-title"
          size="1.2rem"
          // @ts-expect-error
          mt="16px"
          family="bold"
          muted
        >
          {modalErrorTitle}
        </Text>
        {/* @ts-expect-error */}
        <Text size="1rem" align="center" mt="10px" color={color.dimgray}>
          {description || errorMessage}
        </Text>
        <If condition={isShowButton}>
          <Button
            height="46px"
            type="primary"
            // @ts-expect-error
            mt="20px"
            onClick={handleConfirmClick}
            style={{ borderRadius: 4 }}
            width={isFullWidthButton ? '100%' : 'auto'}
          >
            {buttonText}
          </Button>
        </If>
      </Flex>
    </Modal>
  );
};

export default ModalError;
